import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import ActionComp from "../layouts/ActionComp";
import GridMaster from "../masters/GridMaster";
import ActionRenderer from "../masters/ActionRenderer";
import UpdateSalesOrderModal from "./UpdateSalesOrderModal";
import UpdateSalesOrderHistoryModal from "./UpdateSalesOrderHistoryModal";
import { AuthContext } from "../../contexts/AuthContext";
import { useDispatch } from "react-redux";
import { menuActions } from "../../store/menu-slice";
import { searchActions } from "../../store/grid-slice";
import { formatDateDD_MM_YYYY, formatDateInDDMMYYYY, convertToStandardDateTime, formatDateInYYYYMMDD } from "../../functions/common-functions";
import { getFrozenEnquiries } from "../../services/maintain-bid-service";
import { sortByPropertyInAscending } from "../../utils/Utility";

const UpdateSalesOrder = () => {
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  dispatch(menuActions.setMenu("Update Sales Order"));

  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [reload, setReload] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryEnquiry, setSelectedHistoryEnquiry] = useState(null);

  useEffect(() => {
    fetchFrozenEnquiries();
  }, [reload]);

  const fetchFrozenEnquiries = async () => {
    setLoading(true);
    dispatch(searchActions.setSearch(""));
    try {
      const data = await getFrozenEnquiries();

      // Custom sorting: Non-updated sales orders first, then updated ones
      // Within each group, sort by enquiry number ascending
      const sortedData = data?.sort((a, b) => {
        const aHasSO = a.SONumber && a.SONumber.trim() !== '';
        const bHasSO = b.SONumber && b.SONumber.trim() !== '';

        // If one has SO and other doesn't, non-SO comes first
        if (aHasSO !== bHasSO) {
          return aHasSO ? 1 : -1;
        }

        // Both have same SO status, sort by enquiry number
        return a.EnquiryNumber.localeCompare(b.EnquiryNumber);
      });

      setEnquiries(sortedData);
      setExcelData(sortedData);
      setResetFilter(!resetFilter);
    } catch (error) {
      console.error("Error fetching frozen enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filePath, fileName) => {
    if (!filePath) return;

    try {
      const response = await fetch(`/api/files/download?filePath=${encodeURIComponent(filePath)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${context.token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || filePath.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Download failed:', response.statusText);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleUpdateSalesOrder = (row) => {
    setSelectedEnquiry(row);
    setShowModal(true);
  };

  const handleShowHistory = (row) => {
    setSelectedHistoryEnquiry(row);
    setShowHistoryModal(true);
  };

  const columnDefs = [
    {
      headerName: "Enquiry Number",
      field: "EnquiryNumber",
      minWidth: 200,
      tooltipField: "EnquiryNumber",
    },
    {
      headerName: "Enquiry Date",
      field: "InsertedOn",
      minWidth: 150,
      tooltipField: "InsertedOn",
      // valueFormatter: (params) => {
      //   return params.value ? formatDateInDDMMYYYY(params.value) : "";
      // },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const dateAsString = cellValue;
          if (dateAsString == null) {
            return 0;
          }
          // In the example application, dates are stored as dd/mm/yyyy
          // We create a Date object for comparison against the filter date
          const dateCell = new Date(formatDateInYYYYMMDD(cellValue) + " 00:00:00");

          if (dateCell < filterLocalDateAtMidnight) {
            return -1;
          } else if (dateCell > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      headerName: "Due Date",
      field: "DueDate",
      minWidth: 150,
      tooltipField: "DueDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const dateAsString = cellValue;
          if (dateAsString == null) {
            return 0;
          }
          const dateCell = new Date(formatDateInYYYYMMDD(cellValue) + " 00:00:00");

          if (dateCell < filterLocalDateAtMidnight) {
            return -1;
          } else if (dateCell > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      headerName: "Sales Order Number",
      field: "SONumber",
      minWidth: 200,
      tooltipField: "SONumber",
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 200,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 200,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Item Detail",
      field: "ItemDetail",
      minWidth: 200,
      tooltipField: "ItemDetail",
    },
    {
      headerName: "Item Type",
      field: "ItemType",
      minWidth: 150,
      tooltipField: "ItemType",
    },
    {
      headerName: "Quantity",
      field: "Quantity",
      minWidth: 100,
      tooltipField: "Quantity",
    },
    {
      headerName: "UOM",
      field: "UOM",
      minWidth: 100,
      tooltipField: "UOM",
    },
    {
      headerName: "GST%",
      field: "GST",
      minWidth: 100,
      tooltipField: "GST",
    },
    {
      headerName: "Product Class",
      field: "ProductClass",
      minWidth: 100,
      tooltipField: "ProductClass",
    },
    {
      headerName: "Commodity Code",
      field: "CommodityCode",
      minWidth: 100,
      tooltipField: "CommodityCode",
    },
    {
      headerName: "Division",
      field: "Division",
      minWidth: 150,
      tooltipField: "Division",
    },
    {
      headerName: "Location",
      field: "Location",
      minWidth: 200,
      tooltipField: "Location",
    },
    {
      headerName: "LogisticCompany",
      field: "LogisticCompany",
      minWidth: 100,
      tooltipField: "LogisticCompany",
    },
    {
      headerName: "Attachment 1",
      field: "AttachDocument1",
      minWidth: 200,
      cellRenderer: (params) => {
        const file = params.data?.AttachDocument1File;
        return file?.path ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(file.path, params.data.AttachDocument1);
            }}
            style={{ color: '#810055', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.data.AttachDocument1 || 'Download'}
          </a>
        ) : "";
      },
    },
    {
      headerName: "Attachment 2",
      field: "AttachDocument2",
      minWidth: 200,
      cellRenderer: (params) => {
        const file = params.data?.AttachDocument2File;
        return file?.path ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(file.path, params.data.AttachDocument2);
            }}
            style={{ color: '#810055', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.data.AttachDocument2 || 'Download'}
          </a>
        ) : "";
      },
    },
    {
      headerName: "Attachment 3",
      field: "AttachDocument3",
      minWidth: 200,
      cellRenderer: (params) => {
        const file = params.data?.AttachDocument3File;
        return file?.path ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(file.path, params.data.AttachDocument3);
            }}
            style={{ color: '#810055', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.data.AttachDocument3 || 'Download'}
          </a>
        ) : "";
      },
    },
    {
      headerName: "Attachment 4",
      field: "AttachImage",
      minWidth: 200,
      cellRenderer: (params) => {
        const file = params.data?.AttachImageFile;
        return file?.path ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(file.path, params.data.AttachImage);
            }}
            style={{ color: '#810055', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.data.AttachImage || 'Download'}
          </a>
        ) : "";
      },
    },
    {
      headerName: "Attachment 5",
      field: "ImagesAttachment",
      minWidth: 200,
      cellRenderer: (params) => {
        const file = params.data?.ImagesAttachmentFile;
        return file?.path ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownload(file.path, params.data.ImagesAttachment);
            }}
            style={{ color: '#810055', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.data.ImagesAttachment || ''}
          </a>
        ) : "";
      },
    },
    {
      headerName: "Contract/Non-Contract",
      field: "EnquirySource",
      minWidth: 150,
      valueFormatter: (params) => {
        const value = params.value;
        if (value === "Manual" || value === "Disposal") return "Non-Contract";
        if (value === "Contract") return "Contract";
        return value || "";
      },
    },
    {
      headerName: "BP Name",
      field: "BPName",
      minWidth: 200,
      tooltipField: "BPName",
    },
    {
      headerName: "Final Bid (₹)",
      field: "CurrentBid",
      minWidth: 150,
      tooltipField: "CurrentBid",
      valueFormatter: (params) => {
        return params.value ? `₹${params.value}` : "";
      },
    },
    {
      headerName: "Final Bid Date",
      field: "CurrentBidDate",
      minWidth: 150,
      tooltipField: "CurrentBidDate",
      valueFormatter: (params) => {
        return params.value || "";
      },
    },
    {
      headerName: "Customer Name",
      field: "CustomerUserName",
      minWidth: 150,
      tooltipField: "CustomerUserName",
    },
    {
      headerName: "Actions",
      field: "",
      minWidth: 220,
      cellRenderer: (params) => {
        return (
          <ActionRenderer
            selectedData={params.data}
            handleUpdateSalesOrder={() => handleUpdateSalesOrder(params.data)}
            handleShowHistory={() => handleShowHistory(params.data)}
          />
        );
      },
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    },
  ];

  const headerName = (field) => {
    const headerMap = {
      "EnquiryNumber": "Enquiry Number",
      "InsertedOn": "Enquiry Date",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "ItemDetail": "Item Detail",
      "ItemType": "Item Type",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST": "GST%",
      "ProductClass": "Product Class",
      "CommodityCode": "Commodity Code",
      "Division": "Division",
      "Location": "Location",
      "LogisticCompany": "Logistic Company",
      "AttachDocument1": "Attachment 1",
      "AttachDocument2": "Attachment 2",
      "AttachDocument3": "Attachment 3",
      "AttachImage": "Image Attachment",
      "DueDate": "Due Date",
      "EnquirySource": "Contract/Non-Contract",
      "BPName": "BP Name",
      "CurrentBid": "Final Bid (₹)",
      "CustomerUserName": "Customer Name"
    };
    return headerMap[field] || "No match found";
  };

  const fieldName = (header) => {
    const fieldMap = {
      "Enquiry Number": "EnquiryNumber",
      "Enquiry Date": "InsertedOn",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Item Detail": "ItemDetail",
      "Item Type": "ItemType",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST%": "GST",
      "Product Class": "ProductClass",
      "Commodity Code": "CommodityCode",
      "Division": "Division",
      "Location": "Location",
      "Logistic Company": "LogisticCompany",
      "Attachment 1": "AttachDocument1",
      "Attachment 2": "AttachDocument2",
      "Attachment 3": "AttachDocument3",
      "Image Attachment": "AttachImage",
      "Due Date": "DueDate",
      "Contract/Non-Contract": "EnquirySource",
      "BP Name": "BPName",
      "Final Bid (₹)": "CurrentBid",
      "Customer Name": "CustomerUserName"
    };
    return fieldMap[header] || "No match found";
  };

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <ActionComp
          label={"Update Sales Order"}
          instructionText="Frozen enquiries available for sales order update"
        />

        <GridMaster
          rowDetails={enquiries}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={() => { }}
          getFilterColLength={() => { }}
          setExcelData={setExcelData}
          resetind={resetFilter}
          pagination={true}
        />
      </Container>

      {showModal && (
        <UpdateSalesOrderModal
          show={showModal}
          onHide={() => setShowModal(false)}
          enquiry={selectedEnquiry}
          setReload={setReload}
          reload={reload}
          allEnquiries={enquiries}
        />
      )}

      {showHistoryModal && (
        <UpdateSalesOrderHistoryModal
          show={showHistoryModal}
          onHide={() => setShowHistoryModal(false)}
          enquiry={selectedHistoryEnquiry}
        />
      )}
    </>
  );
};

export default UpdateSalesOrder;